/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import {
  Grid,
  Button,
  Stack,
  Typography,
  Paper,
  Box,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  DeleteOutlined,
  Download,
  InsertDriveFileOutlined,
  NavigateBefore,
  NavigateNext,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useFormik } from "formik";
import { useDispatchWrapper } from "../../../hooks";
import {
  fetchAllMarketCampaignAudienceListAsync,
  fetchMarketCampaignAudienceAsync,
  upsertDraftStepAsync,
  upsertMarketCampaignAudienceAsync,
} from "../../../redux/marketCampaign/marketCampaignActions";
import { LoadState } from "../../../constants/enums";
import { PageLoader } from "../../../components/PageLoader/PageLoader";
import { downLoadFile } from "../../../components/FileUpload/utils";
import { showMessage } from "../../../redux/messages/messagesActions";
import { useBillingCompanyContext } from "../../../providers";
import { useMarketCampaignContext } from "../MarketCampaign.context-state";
import { useReduxStore } from "../../../redux/store.hooks";
import { CreateAudienceRightPanel } from "../../marketingCampaign/components/CreateAudienceRightPanel";
import { MarketingCampaignTemplateRuleList } from "../../marketingCampaign/components/marketCampaignRuleTemplateList";
import { PreviewSelectedAudieneRightPanel } from "../components";
import { ManageAudienceList } from "../components/ManageAudienceList";
import { areAllTemplateVariablesFilled } from "../helpers/step2Helpers";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";

/* -------------------- Subcomponents -------------------- */
const ChooseDataSource = ({
  onSelectDatabase,
  onSelectAudience,
}: {
  onSelectDatabase: () => void;
  onSelectAudience: () => void;
}) => (
  <>
    <Box sx={{ textAlign: "center", mb: 4 }}>
      <Typography
        variant="h3"
        fontWeight={700}
        color="primary.main"
        sx={{ mb: 2 }}
      >
        Choose Your Data Source
      </Typography>
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{ maxWidth: "600px", mx: "auto", lineHeight: 1.6 }}
      >
        Define who will receive your campaign. Select from existing rules or
        create a custom audience.
      </Typography>
    </Box>

    <Box
      display="flex"
      width="100%"
      gap={4}
      justifyContent="center"
      alignItems="stretch"
      sx={{ flexDirection: { xs: "column", md: "row" } }}
    >
      {/* Database Rules */}
      <DataSourceCard
        title="Use Database Rules"
        description="Select from pre-defined audience rules in our database. Perfect for standard targeting criteria."
        buttonLabel="Browse Rules"
        onClick={onSelectDatabase}
      />

      {/* Custom Audience */}
      <DataSourceCard
        title="Use Custom Audience"
        description="Build your own audience from scratch or select from existing saved audiences. Full control over targeting criteria."
        buttonLabel="Select Audience"
        onClick={onSelectAudience}
      />
    </Box>
  </>
);

const DataSourceCard = ({
  title,
  description,
  buttonLabel,
  onClick,
}: {
  title: string;
  description: string;
  buttonLabel: string;
  onClick: () => void;
}) => (
  <Paper
    variant="outlined"
    sx={{
      flex: 1,
      maxWidth: "500px",
      border: "2px solid",
      borderColor: "primary.light",
      borderRadius: 3,
      transition: "all 0.3s ease",
      "&:hover": {
        borderColor: "primary.main",
        boxShadow: 3,
        transform: "translateY(-2px)",
      },
    }}
  >
    <Stack
      direction="column"
      spacing={3}
      alignItems="center"
      justifyContent="center"
      px={4}
      py={5}
      sx={{ minHeight: "320px", textAlign: "center" }}
    >
      <Typography variant="h5" fontWeight={600} color="primary.main">
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ lineHeight: 1.6 }}
      >
        {description}
      </Typography>

      <Button
        variant="contained"
        color="primary"
        size="large"
        endIcon={<NavigateNext />}
        onClick={onClick}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "1rem",
          fontWeight: 600,
        }}
      >
        {buttonLabel}
      </Button>
    </Stack>
  </Paper>
);

const HelpSection = () => (
  <Box sx={{ mt: 6, textAlign: "center" }}>
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        💡 Need Help Choosing?
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        <HelpColumn
          title="Use Rule Templates if:"
          color="primary.main"
          items={[
            "You want quick setup with proven targeting",
            "You're new to audience building",
            "You need standard demographic targeting",
          ]}
        />
        <HelpColumn
          title="Create Custom Audience if:"
          color="secondary.main"
          items={[
            "You have specific targeting requirements",
            "You want to reuse existing audiences",
            "You need advanced filtering options",
          ]}
        />
      </Box>
    </Paper>
  </Box>
);

const HelpColumn = ({
  title,
  color,
  items,
}: {
  title: string;
  color: string;
  items: string[];
}) => (
  <Box sx={{ textAlign: "left", maxWidth: "300px" }}>
    <Typography variant="body2" fontWeight={600} color={color} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {items.map((item, idx) => (
        <React.Fragment key={idx}>
          • {item}
          <br />
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);

/* -------------------- Main Component -------------------- */
export const Step2MarketCampaignAudience = React.memo(() => {
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const { marketCampaign, state, onStateChange, draftSteps, isEditable, isAutoGenerating, onKeyChange } =
    useMarketCampaignContext();

  const { list: ruleList } = useSelector(
    (storeState: IStoreState) =>
      storeState.marketCampaign.marketCampaignRuleTemplate,
  );

  const { data: audienceData, dataLoading } =
    useReduxStore("marketCampaign").marketCampaignAudience;

  const [audienceState, setAudienceState] = useState<{
    import_type: "DATABASE" | "AUDIENCE" | null;
    view_audience_list: boolean;
    preview_audience_dialog: boolean;
    create_audience_dialog: boolean;
  }>({
    import_type: null,
    view_audience_list: false,
    preview_audience_dialog: false,
    create_audience_dialog: false,
  });

  const { values, setValues, setFieldValue } = useFormik({
    initialValues: {
      ...audienceData,
      market_campaign_uuid: audienceData.market_campaign_uuid || marketCampaign.market_campaign_uuid,
      company_outlet_uuid: audienceData.company_outlet_uuid || companyOutlet.comapny.company_outlet_uuid,
    },
    enableReinitialize: true,
    onSubmit: (vals) => {
      dispatch(upsertMarketCampaignAudienceAsync(vals, () => handleNextStep()));
    },
  });

  const resetValues = () => {
    setValues({
      ...audienceData,
      ...(!values.market_campaign_uuid && {
        market_campaign_uuid: marketCampaign.market_campaign_uuid,
      }),
      ...(!values.company_outlet_uuid && {
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      }),
    });
  };

  /* ---------- variables details logic ---------- */

  const selectedTemplate = React.useMemo(() => {
    if (!values.market_campaign_rule_template_uuid) return null;

    return (
      ruleList?.find((t) => t.market_campaign_rule_template_uuid === values.market_campaign_rule_template_uuid) || null
    );
  }, [values.market_campaign_rule_template_uuid, ruleList]);

  const canShowSaveNext = React.useMemo(() => {
    if (!selectedTemplate) return false;

    return areAllTemplateVariablesFilled(
      selectedTemplate.variables_details,
      values.campaign_rule,
    );
  }, [selectedTemplate, values.campaign_rule]);

  /* ---------- Handlers ---------- */
  const handleSaveAndNext = () => {
    dispatch(
      upsertMarketCampaignAudienceAsync(values, () => {
        dispatch(
          upsertDraftStepAsync(
            {
              ...draftSteps,
              current_step: 2,
              draft_step_data: {
                ...draftSteps.draft_step_data,
                step_two_data: {
                  database_rules: values,
                  type: "DATABASE_RULES",
                },
              },
            },
            (draftState) => {
              onKeyChange("draftSteps", draftState);
            },
          ),
        );

        handleNextStep();
      }),
    );
  };

  const handleFileUploadSuccess = (path: string) => {
    dispatch(
      upsertMarketCampaignAudienceAsync(
        { ...values, audience_source: "EXCEL", source_link: [path] },
        () => { },
      ),
    );
  };

  const handleNextStep = () => {
    onStateChange({ ...state, activeStep: 2, maxAllowedSteps: 2 });
  };

  const handleBackToSourceClick = () => {
    setAudienceState((prev) => ({
      ...prev,
      import_type: null,
      view_audience_list: false,
      preview_audience_dialog: false,
      create_audience_dialog: false,
    }));
  };

  /* ---------- Effects ---------- */
  React.useEffect(() => {
    if (!marketCampaign.market_campaign_uuid) return;
    dispatch(
      fetchMarketCampaignAudienceAsync(marketCampaign.market_campaign_uuid, (isSuccess, dataFound) => {
          if (!isSuccess || !dataFound) return;
          if (dataFound.audience_source === "DATABASE") {
            setAudienceState((prev) => ({
              ...prev,
              import_type: "DATABASE",
            }));
          } else {
            setAudienceState((prev) => ({
              ...prev,
              import_type: "AUDIENCE",
              view_audience_list: true,
            }));
            dispatch(
              fetchAllMarketCampaignAudienceListAsync(
                1, // pageNumber
                25, // rowsInPerPage
                25, // limit
                "-1", // status
                { fromDate: "", toDate: "" }, // date
                [], // searchValue
                companyOutlet.comapny.company_outlet_uuid,
                undefined, // searchByName
                dataFound.market_campaign_audience_list_uuid,
              ),
            );
            // commented this bcox dont want to auto open the panel
            // .then(() => {
            //   setAudienceState((prev) => ({
            //     ...prev,
            //     preview_audience_dialog: true,
            //   }));
            // });
          }
        },
      ),
    );
  }, [marketCampaign.market_campaign_uuid]);

  /* ---------- Render ---------- */
  return (
    <PageLoader loading={dataLoading === LoadState.InProgress}>
      <Grid container spacing={1} justifyContent="center" alignContent="center">
        <Grid item xs={12} md={10}>
          {!audienceState.view_audience_list && (
            <>
              {!audienceState.import_type && (
                <>
                  <ChooseDataSource
                    onSelectDatabase={() => {
                      setAudienceState((prev) => ({
                        ...prev,
                        import_type: "DATABASE",
                      }));
                      setValues({ ...values, audience_source: "DATABASE" });
                    }}
                    onSelectAudience={() => {
                      setAudienceState((prev) => ({
                        ...prev,
                        import_type: "AUDIENCE",
                        view_audience_list: true,
                      }));
                    }}
                  />
                  <HelpSection />
                </>
              )}

              {audienceState.import_type === "DATABASE" && (
                <>
                  <MarketingCampaignTemplateRuleList
                    values={values}
                    setFieldValue={setFieldValue}
                    marketCampaignId={marketCampaign.market_campaign_uuid}
                    isDisabled={draftSteps?.draft_step_data?.step_two_data?.lock === 1 || !isEditable || isAutoGenerating}
                    seValues={(vals) => setValues(vals)}
                  />

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    mt={4}
                  >
                    <Button
                      variant="contained"
                      startIcon={<NavigateBefore />}
                      disabled={
                        draftSteps?.draft_step_data?.step_two_data?.lock === 1 || !isEditable || isAutoGenerating
                      }
                      onClick={() => {
                        setAudienceState((prev) => ({
                          ...prev,
                          import_type: null,
                          view_audience_list: true,
                          create_audience_dialog: false,
                          preview_audience_dialog: false,
                        }));
                      }}
                    >
                      Back to Data Source
                    </Button>

                    {canShowSaveNext && (
                      <Button
                        variant="contained"
                        endIcon={<NavigateNext />}
                        onClick={handleSaveAndNext}
                        disabled={
                          draftSteps?.draft_step_data?.step_two_data?.lock === 1 || !isEditable || isAutoGenerating
                        }
                      >
                        Save & Next
                      </Button>
                    )}

                    {(draftSteps?.draft_step_data?.step_two_data?.lock === 1 ||
                      !isEditable ||
                      isAutoGenerating ||
                      (values.market_campaign_audience_unique_id != null &&
                        marketCampaign.sending_type !== "QUICK")) && (
                      <Button
                        variant="contained"
                        endIcon={<NavigateNext />}
                        onClick={handleNextStep}
                      >
                        Next
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </>
          )}

          {/* Audience List */}
          {audienceState.view_audience_list && (
            <ManageAudienceList
              onRowSelect={(selectedAudience) =>
                setValues((prev) => ({
                  ...prev,
                  ...selectedAudience,
                  market_campaign_uuid: marketCampaign.market_campaign_uuid,
                }))
              }
              onNext={handleNextStep}
              onBackClick={handleBackToSourceClick}
            />
          )}
        </Grid>
      </Grid>

      {/* Panels */}
      {audienceState.preview_audience_dialog &&
        audienceData?.market_campaign_audience_list_uuids && (
          <PreviewSelectedAudieneRightPanel
            open
            onClose={() => {
              setAudienceState((prev) => ({
                ...prev,
                import_type: null,
                create_audience_dialog: false,
                preview_audience_dialog: false,
              }));
              resetValues();
            }}
            onComplete={() => {
              setAudienceState((prev) => ({
                ...prev,
                preview_audience_dialog: false,
              }));
            }}
            mode="VIEW_AUDIENCE"
          />
        )}

      {audienceState.create_audience_dialog && (
        <CreateAudienceRightPanel
          open
          onClose={() => {
            setAudienceState((prev) => ({
              ...prev,
              import_type: null,
              create_audience_dialog: false,
              preview_audience_dialog: false,
            }));
            resetValues();
          }}
          heading="Template"
          type="TEMPLATE"
          fileUploadSuccess={handleFileUploadSuccess}
        />
      )}
    </PageLoader>
  );
});

/* -------------------- File Display -------------------- */
export const SingleFileDisplay: React.FC<{
  url: string;
  onDelete: () => void;
  onViewClick: () => void;
}> = ({ onDelete, onViewClick, url }) => {
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);

  const handleDownloadFile = async () => {
    try {
      setLoading(true);
      await downLoadFile(url);
    } catch {
      dispatch(
        showMessage({
          displayAs: "snackbar",
          message: "Something went wrong!",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      borderRadius={2}
      position="relative"
      p={2}
      spacing={2}
      sx={{
        backgroundColor: "background.paper",
        boxShadow: 1,
        borderColor: "divider",
      }}
    >
      {/* File Icon */}
      <Box
        sx={{
          height: 44,
          borderRadius: 1,
          backgroundColor: "primary.light",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <InsertDriveFileOutlined
          fontSize="medium"
          sx={{ color: "primary.main" }}
        />
      </Box>

      {/* File Name */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {url}
        </Typography>
      </Box>

      {/* Progress */}
      {loading && (
        <LinearProgress
          color="primary"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
        />
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
        <Tooltip title="Preview">
          <span>
            <IconButton size="small" onClick={onViewClick} disabled={loading}>
              <VisibilityOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton
              size="small"
              onClick={handleDownloadFile}
              disabled={loading}
            >
              <Download fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton size="small" onClick={onDelete} disabled={loading}>
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
};
